"use client";

import {
  Component,
  type ComponentProps,
  type ComponentType,
  type LazyExoticComponent,
  lazy,
  type ReactNode,
  useState,
} from "react";
import { ChunkRetry } from "@/components/chunk-retry";
import { importFromUrl } from "./import-url";
import { IMPORT_RETRY_DELAYS_MS, isChunkLoadError, retryImport } from "./retry-import";

// biome-ignore lint/suspicious/noExplicitAny: same constraint as React's own `lazy`
interface Attempt<T extends ComponentType<any>> {
  Loaded: LazyExoticComponent<T>;
  failed: boolean;
}

/** A function component, or a memo / forwardRef / lazy wrapper of one. */
function isComponent(value: unknown): boolean {
  return (
    typeof value === "function" ||
    (typeof value === "object" && value !== null && "$$typeof" in value)
  );
}

const safely = <V,>(fn: () => V): V | undefined => {
  try {
    return fn();
  } catch {
    return undefined;
  }
};

/** Errors that came from the chunk LOAD (as opposed to the loaded component's own render). */
const loadErrors = new WeakSet<object>();

/**
 * `React.lazy` that survives a missing chunk. `widget.js` imports its sibling chunks relative to its
 * own URL; when they are not served next to it (a copied-alone `widget.js`, a stale cached entry after
 * a deploy, offline) a bare `lazy()` rejects and the nearest error boundary takes the widget down.
 *
 * A network failure is retried automatically (capped, with backoff; the surrounding `<Suspense>`
 * fallback stays up meanwhile). When the retries are used up the feature shows a small inline
 * "Couldn't load - Retry"; Retry makes a NEW attempt (a rejected `lazy()` is cached forever, so each
 * attempt is a fresh one, shared by every instance of this chunk). A real error (a bug in the chunk)
 * is logged once and not retried automatically; an error thrown by the loaded component's own render
 * is NOT swallowed - it reaches the nearest outer boundary as before.
 */
interface ChunkOptions<M, T> {
  /** Backoff before each automatic retry (its length is the cap). */
  retryDelays?: readonly number[];
  /** Which export of the loaded module is the component (default: `default`). */
  pick?: (module: M) => T;
}

/* biome-ignore lint/suspicious/noExplicitAny: mirrors React's own `lazy<T extends ComponentType<any>>` constraint */
export function lazyChunk<T extends ComponentType<any>>(
  name: string,
  load: () => Promise<{ default: T }>,
  options?: ChunkOptions<{ default: T }, T>,
): ComponentType<ComponentProps<T>>;
/* biome-ignore lint/suspicious/noExplicitAny: mirrors React's own `lazy<T extends ComponentType<any>>` constraint */
export function lazyChunk<M, T extends ComponentType<any>>(
  name: string,
  load: () => Promise<M>,
  options: ChunkOptions<M, T> & { pick: (module: M) => T },
): ComponentType<ComponentProps<T>>;
// biome-ignore lint/suspicious/noExplicitAny: implementation of the overloads above
export function lazyChunk<M, T extends ComponentType<any>>(
  name: string,
  load: () => Promise<M>,
  options: ChunkOptions<M, T> = {},
): ComponentType<ComponentProps<T>> {
  const delays = options.retryDelays ?? IMPORT_RETRY_DELAYS_MS;
  const pick = options.pick ?? ((m: M) => (m as { default: T }).default);

  const newAttempt = (): Attempt<T> => {
    const attempt: Attempt<T> = {
      failed: false,
      Loaded: lazy(async () => {
        try {
          const module = await retryImport(
            load,
            delays,
            (url, n) => importFromUrl<M>(url, n),
            (m) => isComponent(safely(() => pick(m))),
          );
          return { default: pick(module) };
        } catch (raw) {
          const error = raw instanceof Error ? raw : new Error(String(raw));
          attempt.failed = true;
          loadErrors.add(error);
          if (isChunkLoadError(error)) {
            console.warn(
              `[wexio] the "${name}" chunk failed to load; that feature is unavailable until it is retried`,
              error,
            );
          } else {
            console.error(`[wexio] the "${name}" chunk threw while loading`, error);
          }
          throw error;
        }
      }),
    };
    return attempt;
  };

  let current = newAttempt();
  const reload = () => {
    if (current.failed) current = newAttempt();
  };

  return function LazyChunk(props: ComponentProps<T>) {
    const [round, setRound] = useState(0);
    const Loaded = current.Loaded as unknown as ComponentType<ComponentProps<T>>;
    return (
      <LoadBoundary
        key={round}
        onRetry={() => {
          reload();
          setRound((r) => r + 1);
        }}
      >
        <Loaded {...props} />
      </LoadBoundary>
    );
  };
}

class LoadBoundary extends Component<
  { children: ReactNode; onRetry: () => void },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    // Not a failed load: the loaded component itself threw. Do not swallow it.
    if (!loadErrors.has(error)) throw error;
    return <ChunkRetry onRetry={this.props.onRetry} />;
  }
}
